import { JC_LOADING } from '../utils/JcLoading';
import { isLoading } from '../utils/JcLoading';
import { JcAsyncResult } from './JcAsyncResult';

/**
 * The settled `value` tuple of a list of results: each slot is that result's
 * own `T` (so a doc result's `T | null` keeps its `null`, a list result's `T[]`
 * stays non-null).
 */
type Values<R extends ReadonlyArray<JcAsyncResult<unknown>>> = {
    [K in keyof R]: R[K] extends JcAsyncResult<infer T> ? T : never;
};

/**
 * Combines several {@link JcAsyncResult}s into one. `value` is `JC_LOADING` while
 * any input is loading; the first input `error` wins (so a single notice shows);
 * `retry()` re-runs every input (idempotent — a healthy listener just re-yields).
 * Once settled, `value` is the tuple of each input's value.
 */
export const combineAsync = <R extends ReadonlyArray<JcAsyncResult<unknown>>>(
    ...results: R
): JcAsyncResult<Values<R>> => {
    const error = results.find((r) => r.error !== null)?.error ?? null;
    const anyLoading = results.some((r) => isLoading(r.value));
    const value = anyLoading ? JC_LOADING : (results.map((r) => r.value) as Values<R>);

    return {
        value,
        error,
        retry: () => results.forEach((r) => r.retry()),
    };
};
